import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  activatePlanByIdService,
  getPatientDiagnosesService,
  getPlanByIdService,
  getUserByIdService,
  rejectPlanByIdService,
} from "../api/services";
import { PlanDetails } from "../components/PlanDetails";
import { PlanStatusEnum } from "../constants/enums";
import { useRequestError } from "../hooks/useRequestError";
import { useAuthStore } from "../stores/authStore";
import type { DiagnosisModel, PlanModel, UserModel } from "../types/models";

const fullName = (user?: UserModel | null) => {
  if (!user) return "—";
  return `${user.first_name || ""} ${user.last_name || ""}`.trim() || "—";
};

export const PlanPage = () => {
  const { planId = "" } = useParams();
  const navigate = useNavigate();
  const { claims } = useAuthStore();
  const { handleRequestError } = useRequestError();
  const [plan, setPlan] = useState<PlanModel | null>(null);
  const [patient, setPatient] = useState<UserModel | null>(null);
  const [doctor, setDoctor] = useState<UserModel | null>(null);
  const [caregiver, setCaregiver] = useState<UserModel | null>(null);
  const [diagnoses, setDiagnoses] = useState<DiagnosisModel[]>([]);
  const [loading, setLoading] = useState(false);

  const me = claims?.sub;
  const canEdit = Boolean(plan?.patient_id && plan.patient_id !== me);
  const canActOnPending = Boolean(
    plan &&
      plan.status === PlanStatusEnum.PENDING &&
      plan.doctor_id &&
      plan.doctor_id === me
  );

  const load = async () => {
    if (!planId) return;
    setLoading(true);
    try {
      const res = await getPlanByIdService(planId);
      setPlan(res);

      const staffLoads: Promise<void>[] = [];

      if (res.patient_id) {
        staffLoads.push(
          getUserByIdService(res.patient_id)
            .then(setPatient)
            .catch(() => setPatient(null))
        );
        staffLoads.push(
          getPatientDiagnosesService(res.patient_id)
            .then((list) => setDiagnoses(list || []))
            .catch(() => setDiagnoses([]))
        );
      } else {
        setPatient(null);
        setDiagnoses([]);
      }

      if (res.doctor_id) {
        staffLoads.push(
          getUserByIdService(res.doctor_id)
            .then(setDoctor)
            .catch(() => setDoctor(res.doctor || null))
        );
      } else {
        setDoctor(res.doctor || null);
      }

      if (res.caregiver_id) {
        staffLoads.push(
          getUserByIdService(res.caregiver_id)
            .then(setCaregiver)
            .catch(() => setCaregiver(res.caregiver || null))
        );
      } else {
        setCaregiver(res.caregiver || null);
      }

      await Promise.all(staffLoads);
    } catch (error) {
      handleRequestError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [planId]);

  const activate = async () => {
    setLoading(true);
    try {
      await activatePlanByIdService(planId);
      navigate(-1);
    } catch (error) {
      handleRequestError(error);
    } finally {
      setLoading(false);
    }
  };

  const reject = async () => {
    if (!window.confirm("Reject this plan?")) return;
    setLoading(true);
    try {
      await rejectPlanByIdService(planId);
      navigate(-1);
    } catch (error) {
      handleRequestError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Plan View</h1>
          {plan ? <p className="muted">Status: {plan.status}</p> : null}
        </div>
        <div className="actions">
          {canEdit && plan?.patient_id ? (
            <Link
              className="btn btn-secondary"
              to={`/plans/setup?user_id=${plan.patient_id}&plan_id=${planId}`}
            >
              Edit plan
            </Link>
          ) : null}
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
            Back
          </button>
        </div>
      </header>

      {loading && !plan ? <div className="spinner" /> : null}

      {plan ? (
        <>
          <section className="panel stack gap-sm">
            <div className="detail-row">
              <span>Caregiver</span>
              <span>{fullName(caregiver)}</span>
            </div>
            <div className="detail-row">
              <span>Doctor</span>
              <span>{fullName(doctor)}</span>
            </div>
            <div className="detail-row">
              <span>Patient</span>
              <span>{fullName(patient)}</span>
            </div>
          </section>

          <PlanDetails
            description={plan.description}
            startDate={plan.start_date}
            endDate={plan.end_date}
            lifestyle={plan.data?.lifestyle}
            vitals={plan.data?.vitals}
            prescription={plan.data?.prescription}
            diagnoses={diagnoses}
            currentStats={patient?.vital_stats}
          />

          {canActOnPending ? (
            <div className="actions sticky-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={loading}
                onClick={activate}
              >
                Activate plan
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={loading}
                onClick={reject}
              >
                Reject plan
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};
